import React, { useCallback } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, Button } from '../../../../../../Components/MUIs';

/**
 * ProcessRegisterModal Component
 * Step 방식의 공정 등록 모달
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Close handler
 * @param {string} props.registerTargetDepLabel - 공정 선택 팝업에서 선택한 부서명(표시용)
 * @returns {JSX.Element} ProcessRegisterModal component
 */
const ProcessRegisterModal = ({
  open,
  onClose,
  processRegisterData,
  setProcessRegisterData,
  registerTargetDepLabel = '',
  SaveLogic2,
}) => {
  const handleFieldChange = useCallback(
    (field, value) => {
      setProcessRegisterData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [setProcessRegisterData]
  );

  const handleRegister = () => {
    SaveLogic2();
    // 폼 초기화
    setProcessRegisterData({
      depCd: '',
      gongJongNm: '',
    });
  };

  const handleClose = () => {
    // 폼 초기화
    setProcessRegisterData({
      depCd: '',
      gongJongNm: '',
    });
    onClose();
  };

  // TextField onChange를 최적화
  const handleGongJongNmChange = useCallback(
    (e) => {
      handleFieldChange('gongJongNm', e.target.value);
    },
    [handleFieldChange]
  );
  return (
    <Dialog
      title={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.25rem' }}
            >
              공정 등록
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#6B7280',
                fontSize: '0.75rem',
                mt: 0.5,
                display: 'block',
              }}
            >
              공정 선택 화면에서 선택한 부서에 새 공정이 등록됩니다. 공정명만
              입력해 주세요.
            </Typography>
          </Box>
          <Box
            component="button"
            onClick={handleClose}
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              padding: 0,
              '&:hover': {
                color: '#111827',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '1.5rem' }} />
          </Box>
        </Box>
      }
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
      useSaveButton={false}
      useCloseButton={false}
      content={
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {registerTargetDepLabel ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '0.375rem',
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: '#166534' }}
                >
                  등록 부서
                </Typography>
                <Typography variant="body2" sx={{ color: '#15803D', mt: 0.5 }}>
                  {registerTargetDepLabel}
                </Typography>
              </Box>
            ) : null}

            {/* 공정명 입력 */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#111827',
                }}
              >
                공정명
                <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={processRegisterData.gongJongNm}
                onChange={handleGongJongNmChange}
                placeholder="예: 약제 희석 및 배합 작업"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.375rem',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover fieldset': {
                      borderColor: '#3FA73F',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3FA73F',
                      borderWidth: '1px',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      }
      actions={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5, // gap-3 (12px)
            px: 3, // px-6 (24px)
            py: 2.5, // py-5 (20px)
            width: '100%',
            maxWidth: '100%',
            borderTop: '1px solid #E5E7EB',
            backgroundColor: '#F9FAFB', // bg-gray-50
          }}
        >
          <Button
            name="취소 "
            onClick={handleClose}
            sx={{
              flex: 1,
              px: 2.5, // px-5 (20px)
              py: 1.25, // py-2.5 (10px)
              borderRadius: '0.375rem', // rounded-md
              color: '#111827', // text-text-main-light
              backgroundColor: '#fff', // bg-white
              border: '1px solid #E5E7EB', // border-border-light
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // shadow-sm
              fontWeight: 500, // font-medium
              fontSize: '0.875rem', // text-sm
              textTransform: 'none', // 기본 대문자 변환 제거
              '&:hover': {
                backgroundColor: '#F3F4F6', // hover:bg-gray-100
                borderColor: '#E5E7EB',
              },
              transition: 'all 0.2s ease', // transition-colors
            }}
          />
          <Button
            name="등록하기 "
            onClick={handleRegister}
            startIcon={
              <CheckCircleIcon
                sx={{
                  fontSize: '0.875rem', // text-sm
                  marginRight: '-4px', // gap-2를 위한 조정
                }}
              />
            }
            sx={{
              flex: 1,
              px: 2.5, // px-5 (20px)
              py: 1.25, // py-2.5 (10px)
              borderRadius: '0.375rem', // rounded-md
              color: '#fff', // text-white
              backgroundColor: '#3FA73F', // bg-primary
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // shadow-sm
              fontWeight: 500, // font-medium
              fontSize: '0.875rem', // text-sm
              textTransform: 'none', // 기본 대문자 변환 제거
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5, // gap-2 (8px)
              '&:hover': {
                backgroundColor: '#22C55E', // hover:bg-green-600
              },
              transition: 'all 0.2s ease', // transition-colors
              '& .MuiButton-startIcon': {
                marginRight: '0.5rem', // gap-2를 위한 조정
                marginLeft: 0,
              },
            }}
          />
        </Box>
      }
    />
  );
};

export default ProcessRegisterModal;
